// @givmo/employer-module — the sign-up funnel's ADDRESS BOOK and the routing-state → route map
// (07-1.1a; two hosts since EP-MNT8).
//
// @givmo/auth resolves the post-sign-in `AuthRoutingState` (a pure function of company presence +
// email-verified + active); the screens map that state onto the HOST's routes. Ported from the
// Flutter `SignInCubit.navigateAfterSignIn` switch:
//
//   authenticated             → the landing  (honor the guard's `redirect` search param if present)
//   emailUnverified           → the OTP screen
//   pendingManualVerification → the verified / waiting-room screen
//   unregistered              → the sign-up details screen (provider user finishing registration)
//
// ══ ONE FUNNEL, TWO ADDRESS BOOKS (EP-MNT8) ════════════════════════════════════════════════════
//
// The funnel's screens render in two hosts: the employer portal (`apps/admin`), whose doors are
// `/login-home` and `/sign-in` and whose companies land on `/dashboard`, and the company console
// (`apps/company`), whose one door is `/sign-in` and whose new companies land in the Donations
// module at `/donations` (its onboarding wizard). Every address a screen navigates to is read from
// the host's book (`useSignUpHost().paths`), never spelled at the call site. The portal's book is
// the funnel's half of the old `AUTH_ROUTES`, byte for byte; `AUTH_ROUTES` is that book plus the
// portal's other signed-out doors (Account Security's, AS-WEB26/30), so the portal's routes, tests
// and history are unchanged.

import type { AuthRoutingState } from '@givmo/auth';

/** Every address the sign-up funnel navigates to, in one host. */
export interface SignUpPaths {
  /** The host's sign-in chooser — where the sign-up chooser's Back and the code screen's Logout go. */
  readonly loginHome: string;
  /** The email/password sign-in form (it honours `redirect-block`). */
  readonly signIn: string;
  /** The sign-up chooser. */
  readonly signUp: string;
  readonly signUpDetails: string;
  readonly signUpEmailPassword: string;
  readonly signUpOtp: string;
  readonly signUpVerified: string;
  /** EP-SUITE2's request-access screen (one route, two states: request → pending). */
  readonly signUpRequestAccess: string;
  /** Where a company's admin lands once the funnel is done — the portal's dashboard, the console's Donations module. */
  readonly dashboard: string;
}

/** The employer portal's book — `AUTH_ROUTES` as it always was. */
export const PORTAL_SIGN_UP_PATHS = {
  loginHome: '/login-home',
  signIn: '/sign-in',
  signUp: '/sign-up',
  signUpDetails: '/sign-up/details',
  signUpEmailPassword: '/sign-up/email-password',
  signUpOtp: '/sign-up/otp',
  signUpVerified: '/sign-up/verified',
  signUpRequestAccess: '/sign-up/request-access',
  dashboard: '/dashboard',
} as const satisfies SignUpPaths;

/**
 * The company console's book (EP-MNT8). The console has ONE door, so both of the portal's doors map to
 * its `/sign-in`; the funnel keeps the portal's `/sign-up/*` addresses (the forward map in the plan,
 * EP-MNT12, sends the portal's `/sign-up*` here); and a company lands in the Donations module, whose
 * own redirect opens the onboarding wizard for a company with no match program yet.
 */
export const CONSOLE_SIGN_UP_PATHS = {
  loginHome: '/sign-in',
  signIn: '/sign-in',
  signUp: '/sign-up',
  signUpDetails: '/sign-up/details',
  signUpEmailPassword: '/sign-up/email-password',
  signUpOtp: '/sign-up/otp',
  signUpVerified: '/sign-up/verified',
  signUpRequestAccess: '/sign-up/request-access',
  dashboard: '/donations',
} as const satisfies SignUpPaths;

/**
 * The portal's auth route paths under their old name: the funnel's book plus the portal's own
 * signed-out doors that are not the funnel's. Centralised so the portal's screens + tests share one
 * source; a screen inside this package reads its HOST's book instead.
 */
export const AUTH_ROUTES = {
  ...PORTAL_SIGN_UP_PATHS,
  // AS-WEB26 — the way back in for someone whose company locks how they sign in: the chooser's "Lost
  // access to your company sign-in?" opens it. A signed-out page.
  lostCompanyAccess: '/lost-company-access',
  // AS-WEB30 — the organization door's step: the chooser's "Sign in with Organization SSO" opens it.
  // Its return page is `@givmo/passkeys`' `ORGANIZATION_SSO_CALLBACK_PATH`, an operator contract.
  organizationSso: '/sso',
} as const;

/** Where a resolved routing state should send the user. `redirect` honors the guard's search param. */
export function routeForRoutingState(
  state: AuthRoutingState,
  redirect?: string,
  paths: SignUpPaths = PORTAL_SIGN_UP_PATHS,
): string {
  switch (state) {
    case 'authenticated':
      return redirect && redirect.length > 0 ? redirect : paths.dashboard;
    case 'emailUnverified':
      return paths.signUpOtp;
    case 'pendingManualVerification':
      return paths.signUpVerified;
    case 'unregistered':
      return paths.signUpDetails;
  }
}

/**
 * A `navigate(...)` argument for a resolved routing state. Identical targets to
 * {@link routeForRoutingState}, but two cases carry `search: { provider: true }` so the destination
 * screen takes the PROVIDER path EXPLICITLY (rather than inferring it from `isAuthenticated`, which
 * a back-navigated, already-registered email user would also satisfy):
 *   - `unregistered` → the details screen finishes a provider registration (the rebuild's
 *     equivalent of the Flutter `RouteSignUp(isProviderAuth: true)`);
 *   - `emailUnverified` when the sign-in was a PROVIDER one (`opts.viaProvider`) → the OTP screen
 *     must RE-RESOLVE the route after the code confirms instead of assuming the email flow's
 *     registered-and-pending arrival — a microsoft.com sign-in lands there BEFORE any company
 *     exists (GCIP mints Microsoft users unverified; see `needsBackendOtpVerification`).
 */
export function navigationForRoutingState(
  state: AuthRoutingState,
  redirect?: string,
  opts: { viaProvider?: boolean } = {},
  paths: SignUpPaths = PORTAL_SIGN_UP_PATHS,
): { to: string; search?: { provider: true } } {
  if (state === 'unregistered') {
    return { to: paths.signUpDetails, search: { provider: true } };
  }
  if (state === 'emailUnverified' && opts.viaProvider === true) {
    return { to: paths.signUpOtp, search: { provider: true } };
  }
  return { to: routeForRoutingState(state, redirect, paths) };
}
